import { FC, useCallback, useMemo } from "react";

import { MemberName } from "Bands/components";
import { useBandContext } from "Bands/contexts";
import {
  customAccommodationRoomsSelectorForBlock,
  customAccommodationRoomTypesSelectorCreator,
} from "Bands/state";
import { accommodationReservationSelectorCreator } from "Bands/state/data/selectors/accommodationReservation";
import {
  AccommodationRoomType,
  IAccommodationBlock,
  IAccommodationReservation,
  IBandUser,
} from "Bands/types";
import { usePagination } from "Bands/utils";
import { useSelector } from "react-redux";
import { copy } from "shared/assets";
import {
  IActionDropdownItem,
  Loader,
  Pager,
  Table,
  TableColumn,
} from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { IStatus } from "shared/types";
import { mapUserPermissions } from "shared/utils";

const { accommodationBlock: blockCopy } = copy;

interface AccommodationReservationTableProps {
  block: IAccommodationBlock;
  fetchAccommodationReservations: (page: number) => void;
  setShowEditModal?: (
    accommodationReservation?: IAccommodationReservation
  ) => void;
  setShowDeleteModal?: (
    accommodationReservation?: IAccommodationReservation
  ) => void;
  showActions?: boolean;
}

export const AccommodationReservationTable: FC<AccommodationReservationTableProps> =
  ({
    block,
    fetchAccommodationReservations,
    setShowEditModal,
    setShowDeleteModal,
    showActions,
  }) => {
    const { findBandUser, getCategory } = useBandContext();

    const pagination = usePagination(
      API_ENDPOINTS.EVENT_ACCOMMODATION_BLOCK(block.id)
    );

    const reservationsSelector = useMemo(() => {
      const { pages, currentPage } = pagination;

      return accommodationReservationSelectorCreator(pages?.[currentPage]);
    }, [pagination]);

    const reservations = useSelector(reservationsSelector);

    const roomsSelector = useMemo(
      () => customAccommodationRoomsSelectorForBlock(),
      []
    );

    const rooms = useSelector(roomsSelector);

    const customAccommodationRoomTypesSelector = useMemo(
      () => customAccommodationRoomTypesSelectorCreator(block.id),
      [block.id]
    );

    const accommodationRoomTypes = useSelector(
      customAccommodationRoomTypesSelector
    );

    const createRoomCell = useCallback(
      (id: string) => {
        const room = rooms.find(
          (room) => API_ENDPOINTS.EVENT_ACCOMMODATION_ROOM(room.id) === id
        );

        const roomType = accommodationRoomTypes.find(
          (roomType) =>
            API_ENDPOINTS.EVENT_ACCOMMODATION_ROOM_TYPE(roomType.id) ===
            room?.roomType.id
        );

        return (
          (room &&
            roomType &&
            `${
              AccommodationRoomType[
                roomType?.name as keyof typeof AccommodationRoomType
              ] || roomType.name
            } #${room?.roomNumber}`) || <Loader size="s"></Loader>
        );
      },
      [rooms, accommodationRoomTypes]
    );

    const getRowActions = useCallback(
      (accommodationReservationId: string) => {
        const findAccommodationReservation = (
          findId: string
        ): IAccommodationReservation | undefined => {
          return reservations.find((reservation) => reservation.id === findId);
        };

        const accommodationReservation = findAccommodationReservation(
          accommodationReservationId
        );

        if (!accommodationReservation) return [];

        const permissions = mapUserPermissions(block.permissions || []);

        let actions: IActionDropdownItem[] = [];

        if (permissions.EDIT) {
          actions.push({
            label: "Edit",
            dataTestId: "edit_accommodation_reservation_action",
            handleClick: () => {
              setShowEditModal && setShowEditModal(accommodationReservation);
            },
          });
        }

        if (permissions.DELETE) {
          actions.push({
            label: "Delete",
            dataTestId: "delete_accommodation_reservation_action",
            handleClick: () => {
              setShowDeleteModal &&
                setShowDeleteModal(accommodationReservation);
            },
            status: IStatus.error,
          });
        }

        return actions;
      },
      [block.permissions, reservations, setShowDeleteModal, setShowEditModal]
    );

    const getColumns = (): TableColumn[] => [
      {
        Header: blockCopy.create.peopleBlock.fields.name,
        Cell: ({
          row: { original },
        }: {
          row: { original: IAccommodationReservation };
        }) => (
          <MemberName user={findBandUser(original.bandUser.id) as IBandUser} />
        ),
      },
      {
        Header: blockCopy.create.peopleBlock.fields.group,
        Cell: ({
          row: { original },
        }: {
          row: { original: IAccommodationReservation };
        }) =>
          getCategory(original.bandUser.category?.id)?.label ||
          blockCopy.create.peopleBlock.placeholders.noGroup,
      },
      {
        Header: blockCopy.create.peopleBlock.fields.room,
        Cell: ({
          row: { original },
        }: {
          row: { original: IAccommodationReservation };
        }) =>
          original.room
            ? createRoomCell(original.room.id)
            : blockCopy.create.peopleBlock.placeholders.noGroup,
      },
      {
        Header: blockCopy.create.peopleBlock.fields.remark,
        accessor: "remark",
      },
    ];

    return (
      <>
        <Table
          data={reservations}
          columns={getColumns()}
          getRowActions={getRowActions}
          options={{ showActions }}
          emptyCopy={blockCopy.create.peopleBlock.placeholders.emptyPeopleTable}
        />
        <Pager {...pagination} onPageChange={fetchAccommodationReservations} />
      </>
    );
  };
